<template>
    <div class="rounded-xl border border-taupe-200 bg-white p-6 sm:p-7 mb-6">
        <div class="sm:flex sm:items-center sm:justify-between">
            <div>
                <h3 class="text-lg font-medium leading-6 text-taupe-800">
                    Personal Access Tokens
                </h3>
                <p class="mt-1 max-w-2xl text-sm text-taupe-500">
                    Every personal access token in the account. Tokens are
                    created by their owners — you can see who holds one and
                    revoke it, but never the raw value.
                </p>
            </div>
            <div
                class="mt-4 sm:mt-0 sm:ml-4 sm:shrink-0 flex items-center gap-3"
            >
                <label
                    class="flex items-center gap-2 text-sm text-taupe-600 cursor-pointer"
                >
                    <input
                        type="checkbox"
                        v-model="showRevoked"
                        class="h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-500"
                    />
                    Show revoked
                </label>
            </div>
        </div>

        <div class="mt-6">
            <div v-if="loading" class="text-sm text-taupe-500 py-4">
                Loading tokens…
            </div>
            <div
                v-else-if="tokens.length === 0"
                class="rounded-lg border border-dashed border-taupe-300 bg-taupe-50"
            >
                <zen-empty-table-message compact icon="ZenIconToken">
                    No personal access tokens yet.
                </zen-empty-table-message>
            </div>
            <div v-else class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-taupe-200"
                    aria-label="Personal access tokens"
                >
                    <thead>
                        <tr>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Owner
                            </th>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Name
                            </th>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Created
                            </th>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Last used
                            </th>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Expires
                            </th>
                            <th
                                class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th class="py-3" aria-label="Actions"></th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-taupe-100">
                        <tr
                            v-for="token in tokens"
                            :key="token.ulid"
                            class="hover:bg-taupe-50"
                        >
                            <td class="py-3">
                                <div class="text-sm font-medium text-taupe-800">
                                    {{ token.owner?.name ?? '—' }}
                                </div>
                                <div class="text-xs text-taupe-500">
                                    {{ token.owner?.email ?? '' }}
                                </div>
                            </td>
                            <td class="py-3 text-sm font-medium text-taupe-800">
                                {{ token.name }}
                            </td>
                            <td class="py-3 text-sm text-taupe-500">
                                {{ formatDate(token.created_at) }}
                            </td>
                            <td class="py-3 text-sm text-taupe-500">
                                {{
                                    token.last_used_at
                                        ? formatDate(token.last_used_at)
                                        : 'Never'
                                }}
                            </td>
                            <td class="py-3 text-sm text-taupe-500">
                                {{
                                    token.expires_at
                                        ? formatDate(token.expires_at)
                                        : 'Never'
                                }}
                            </td>
                            <td class="py-3">
                                <span
                                    :class="statusPillClass(token.status)"
                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset capitalize"
                                >
                                    {{ token.status }}
                                </span>
                            </td>
                            <td class="py-3 text-right">
                                <zen-button
                                    v-if="token.status !== 'revoked'"
                                    variant="link-danger"
                                    @click="confirmRevoke(token)"
                                    :aria-label="`Revoke token: ${token.name}`"
                                >
                                    Revoke
                                </zen-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <zen-pagination
                v-if="!loading"
                :page="pagination.page"
                :total-pages="pagination.total_pages"
                :total="pagination.total"
                @change="changePage"
            />
        </div>
    </div>

    <revoke-personal-access-token-modal
        :is-open="!!revokeTarget"
        :token="revokeTarget"
        :processing="revoking"
        @close="revokeTarget = null"
        @confirm="revokeToken"
    />
</template>

<script>
import personalAccessTokenService from '@/services/personal-access-token.service'
import { errorMessage, successMessage } from '@/utils/notifications'
import RevokePersonalAccessTokenModal from '@/screens/_components/admin/modals/RevokePersonalAccessTokenModal.vue'
import { useServerPagination } from '@/composables/useServerPagination'
import { removeBy, upsertBy } from '@/utils/list-mutations'

const STATUS_PILL_CLASSES = {
    active: 'bg-green-50 text-green-700 ring-green-600/10',
    suspended: 'bg-amber-50 text-amber-700 ring-amber-600/10',
    revoked: 'bg-taupe-100 text-taupe-500 ring-taupe-500/10',
    expired: 'bg-taupe-100 text-taupe-500 ring-taupe-500/10',
}

export default {
    name: 'PersonalAccessTokensPanel',
    components: {
        RevokePersonalAccessTokenModal,
    },
    setup() {
        const {
            pagination,
            setPage,
            resetPage,
            applyResponse,
            shiftTotal,
            resetOnError,
        } = useServerPagination({ perPage: 25 })
        return {
            pagination,
            setPage,
            resetPage,
            applyResponse,
            shiftTotal,
            resetOnError,
        }
    },
    data() {
        return {
            loading: true,
            tokens: [],
            showRevoked: false,
            revokeTarget: null,
            revoking: false,
        }
    },
    watch: {
        showRevoked() {
            // The filter is applied server-side, so the current page number no
            // longer means anything against the new result set.
            this.resetPage()
            this.loadTokens()
        },
    },
    methods: {
        formatDate(iso) {
            if (!iso) return '—'
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        },
        statusPillClass(status) {
            switch (status) {
                case 'active':
                    return STATUS_PILL_CLASSES.active
                case 'suspended':
                    return STATUS_PILL_CLASSES.suspended
                case 'revoked':
                    return STATUS_PILL_CLASSES.revoked
                case 'expired':
                    return STATUS_PILL_CLASSES.expired
                default:
                    return STATUS_PILL_CLASSES.active
            }
        },
        changePage(page) {
            this.setPage(page)
            this.loadTokens()
        },
        async loadTokens() {
            this.loading = true
            try {
                const response = await personalAccessTokenService.directory({
                    page: this.pagination.page,
                    perPage: this.pagination.per_page,
                    // Revoked tokens are excluded server-side rather than filtered
                    // out of the page here — filtering a page after the fact would
                    // leave short pages and a misleading total.
                    status: this.showRevoked ? undefined : 'active',
                })
                this.tokens = response.data ?? []

                if (this.applyResponse(response.meta)) {
                    return await this.loadTokens()
                }
            } catch {
                errorMessage('Failed to load personal access tokens.')
                this.tokens = []
                this.resetOnError()
            } finally {
                this.loading = false
            }
        },
        confirmRevoke(token) {
            this.revokeTarget = token
        },
        async revokeToken() {
            if (!this.revokeTarget) return
            this.revoking = true
            const target = this.revokeTarget
            try {
                const response =
                    await personalAccessTokenService.revokeForAdmin(target.ulid)
                successMessage('Token revoked.')
                this.revokeTarget = null
                // Active-only view: the token leaves the list. "Show revoked"
                // view: the row stays, with its revoked state from the response.
                // The row may have moved off this page since it was clicked.
                // Then the local list can't be trusted, so reload rather than guess.
                if (!this.tokens.some((token) => token.ulid === target.ulid)) {
                    await this.loadTokens()
                } else if (this.showRevoked) {
                    this.tokens = upsertBy(this.tokens, response.data)
                } else {
                    this.tokens = removeBy(this.tokens, target.ulid)
                    this.shiftTotal(-1)
                    // Emptying a later page leaves nothing to show, so step back
                    // a page. This is the one place a revoke still reloads.
                    if (this.tokens.length === 0 && this.pagination.page > 1) {
                        this.setPage(this.pagination.page - 1)
                        await this.loadTokens()
                    }
                }
            } catch {
                errorMessage('Failed to revoke token.')
            } finally {
                this.revoking = false
            }
        },
    },
    async mounted() {
        await this.loadTokens()
    },
}
</script>
